<template>
  <Drawer
    :aria-label="endpoint ? endpointLabel(endpoint) : 'Endpoint'"
    no-footer
    data-cy="webhook-endpoint-drawer"
    @hide-drawer="emit('close')"
  >
    <template #title>
      <div class="pr-10">
        <template v-if="endpoint">
          <div class="mb-1 flex flex-wrap items-center gap-2">
            <h2
              class="m-0 text-base font-semibold text-gray-900"
              data-cy="webhook-drawer-title"
            >
              {{ endpointLabel(endpoint) }}
            </h2>
            <span
              class="whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold"
              :class="healthStyle(endpoint.health).pill"
              data-cy="webhook-drawer-health"
              >{{ healthStyle(endpoint.health).label }}</span
            >
          </div>
          <p class="m-0 text-sm text-gray-600">
            {{ endpoint.destination.display }} ·
            <code class="text-xs" :title="FINGERPRINT_TITLE">{{
              endpoint.destination.fingerprint
            }}</code>
          </p>
        </template>
        <h2 v-else class="m-0 text-base font-semibold text-gray-900">
          Endpoint
        </h2>
      </div>
    </template>

    <div class="space-y-6 px-4 pb-6">
      <div v-if="loading" class="flex justify-center py-6">
        <Spinner size="small" />
      </div>

      <Alert v-else-if="loadFailed" type="error">
        This endpoint couldn't be loaded.
        <button type="button" class="link ml-1" @click="load">Try again</button>
      </Alert>

      <template v-else-if="endpoint">
        <Alert
          v-if="endpoint.status === 'disabled'"
          type="error"
          data-cy="webhook-drawer-disabled"
        >
          {{ disabledCopy }}
        </Alert>
        <Alert
          v-else-if="endpoint.paused_by_integration"
          type="warning"
          data-cy="webhook-drawer-integration-off"
        >
          Webhooks are turned off, so nothing is sent to this endpoint. Turn
          them on from the Webhooks card in the Marketplace.
        </Alert>
        <Alert
          v-else-if="endpoint.health === 'failing'"
          type="error"
          data-cy="webhook-drawer-failing"
        >
          {{ failingCopy }}
        </Alert>
        <Alert v-if="readOnly" type="info" :text="READ_ONLY_COPY" />
        <Alert
          v-if="isZapier"
          type="info"
          text="Managed in Zapier. Change or turn off this Zap in Zapier."
          data-cy="webhook-drawer-zapier"
        />

        <section>
          <h3 class="mb-1 text-xs font-semibold uppercase text-gray-500">
            Endpoint URL
          </h3>
          <code
            class="block break-all rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-900"
            data-cy="webhook-drawer-url"
            >{{ endpoint.url ?? endpoint.destination.display }}</code
          >
          <a
            :href="`/marketplace/webhooks?tab=deliveries&endpoint_id=${endpoint.id}`"
            class="link mt-1 inline-block text-sm"
            data-cy="webhook-drawer-view-deliveries"
            >View deliveries</a
          >
          <p class="m-0 mt-1 text-xs text-gray-500">
            Subscribed to {{ endpoint.event_types.length }}
            {{ endpoint.event_types.length === 1 ? "event" : "events" }}:
            {{ endpoint.event_types.join(", ") }}
          </p>
        </section>

        <section v-if="canWrite" data-cy="webhook-drawer-actions">
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              :class="SECONDARY"
              data-cy="webhook-drawer-edit"
              @click="emit('edit', endpoint)"
            >
              Edit
            </button>
            <button
              v-if="endpoint.status === 'active'"
              type="button"
              :class="SECONDARY"
              data-cy="webhook-drawer-pause"
              @click="confirming = 'pause'"
            >
              Pause
            </button>
            <button
              v-if="endpoint.status === 'paused'"
              type="button"
              :class="SECONDARY"
              data-cy="webhook-drawer-resume"
              @click="confirming = 'resume'"
            >
              Resume
            </button>
            <button
              v-if="endpoint.status === 'disabled'"
              type="button"
              :class="SECONDARY"
              data-cy="webhook-drawer-enable"
              @click="confirming = 'enable'"
            >
              Enable
            </button>
            <button
              type="button"
              class="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
              data-cy="webhook-drawer-archive"
              @click="confirming = 'archive'"
            >
              Archive
            </button>
          </div>
          <InlineConfirm
            v-if="confirming && confirming !== 'rotate'"
            class="mt-3"
            :message="CONFIRM_COPY[confirming].message"
            :confirm-label="CONFIRM_COPY[confirming].label"
            :danger="confirming === 'archive' || confirming === 'pause'"
            :busy="acting"
            @confirm="runConfirmed"
            @cancel="confirming = null"
          />
        </section>

        <section data-cy="webhook-drawer-secret">
          <h3 class="mb-1 text-xs font-semibold uppercase text-gray-500">
            Signing secret
          </h3>
          <div class="flex flex-wrap items-center gap-2">
            <code
              class="min-w-0 flex-1 break-all rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-900"
              data-cy="webhook-secret-value"
              >{{ secret ?? "whsec_••••••••••••••••••••••••" }}</code
            >
            <button
              v-if="!secret"
              type="button"
              :class="SECONDARY"
              :disabled="revealing"
              data-cy="webhook-secret-reveal"
              @click="reveal"
            >
              {{ revealing ? "Revealing…" : "Reveal" }}
            </button>
            <template v-else>
              <button
                type="button"
                :class="SECONDARY"
                data-cy="webhook-secret-copy"
                @click="copySecret"
              >
                {{ secretCopied ? "Copied" : "Copy" }}
              </button>
              <button
                type="button"
                :class="SECONDARY"
                data-cy="webhook-secret-hide"
                @click="hideSecret"
              >
                Hide
              </button>
            </template>
            <button
              v-if="canWrite"
              type="button"
              :class="SECONDARY"
              data-cy="webhook-secret-rotate"
              @click="confirming = 'rotate'"
            >
              Rotate
            </button>
          </div>
          <p class="m-0 mt-1 text-xs text-gray-500">
            Every reveal is recorded with your name.
          </p>
          <p
            v-if="endpoint.secret_rotating_until"
            class="m-0 mt-2 text-sm text-gray-700"
            data-cy="webhook-secret-rotating"
          >
            Rotation in progress: the previous secret also signs requests until
            {{ formatDateTimeWithTimezone(endpoint.secret_rotating_until) }}.
          </p>
          <InlineConfirm
            v-if="confirming === 'rotate'"
            class="mt-3"
            confirm-label="Rotate secret"
            :busy="acting"
            @confirm="runConfirmed"
            @cancel="confirming = null"
          >
            {{ ROTATION_EXPLANATION }}
            <template v-if="endpoint.secret_rotating_until">
              A rotation is already running: rotating again stops the oldest
              secret from signing right away.</template
            >
          </InlineConfirm>

          <Alert
            type="warning"
            class="mt-3"
            data-cy="webhook-signature-warning"
          >
            <strong>{{ SIGNATURE_WARNING }}</strong>
            Sera can't tell whether your endpoint checks the signature, so make
            sure it does.
          </Alert>
          <p class="m-0 mb-2 mt-3 text-sm text-gray-700">
            Verify each request with a
            <a
              href="https://www.standardwebhooks.com/"
              target="_blank"
              rel="noopener"
              class="link"
              >Standard Webhooks</a
            >
            library and this secret. See
            <a
              href="/marketplace/webhooks?tab=events&section=verifying"
              class="link"
              data-cy="webhook-drawer-verify-docs"
              >Verifying webhooks</a
            >
            for the details.
          </p>
          <VerificationSnippet />
          <p
            class="m-0 mt-2 text-xs text-gray-500"
            data-cy="webhook-rotation-explanation"
          >
            {{ ROTATION_EXPLANATION }}
          </p>
        </section>

        <section v-if="canWrite" data-cy="webhook-drawer-test">
          <h3 class="mb-1 text-xs font-semibold uppercase text-gray-500">
            Send a test event
          </h3>
          <p class="m-0 mb-2 text-sm text-gray-600">
            Sends the example payload for an event, marked
            <code class="text-xs">"origin": "test"</code>. It's signed, retried
            and logged like any delivery.
          </p>
          <div class="flex flex-wrap items-center gap-2">
            <label for="webhook-test-event" class="sr-only">Event</label>
            <select
              id="webhook-test-event"
              v-model="testEventType"
              class="rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm"
              data-cy="webhook-test-event"
            >
              <option
                v-for="eventType in testEventTypes"
                :key="eventType.event_type"
                :value="eventType.event_type"
              >
                {{ eventType.name }} ({{ eventType.event_type }})
              </option>
            </select>
            <button
              type="button"
              :class="SECONDARY"
              :disabled="testing || !sendable"
              data-cy="webhook-test-send"
              @click="sendTest"
            >
              {{ testing ? "Sending…" : "Send test event" }}
            </button>
          </div>
          <p
            v-if="!sendable"
            class="m-0 mt-1 text-xs text-gray-500"
            data-cy="webhook-test-unavailable"
          >
            {{ unsendableCopy }}
          </p>
        </section>

        <section data-cy="webhook-drawer-stats">
          <h3 class="mb-2 text-xs font-semibold uppercase text-gray-500">
            Last 7 days
          </h3>
          <p
            v-if="statsFailed"
            class="m-0 text-sm text-gray-500"
            data-cy="webhook-stats-error"
          >
            Delivery stats couldn't be loaded.
          </p>
          <template v-else-if="stats">
            <div
              class="flex h-28 items-end gap-2"
              role="img"
              :aria-label="chartLabel"
              data-cy="webhook-stats-chart"
            >
              <div
                v-for="day in stats.days"
                :key="day.date"
                class="flex h-full flex-1 flex-col items-center justify-end"
                :title="dayTitle(day)"
                :data-cy="`webhook-stats-day:${day.date}`"
              >
                <div
                  class="flex w-full flex-col-reverse overflow-hidden rounded-t"
                  :style="{ height: `${barHeight(day)}%` }"
                >
                  <div
                    v-for="part in BAR_PARTS"
                    :key="part.key"
                    :class="part.color"
                    :style="{ height: `${partShare(day, part.key)}%` }"
                  ></div>
                </div>
                <span class="mt-1 text-[10px] text-gray-500">{{
                  shortDay(day.date)
                }}</span>
              </div>
            </div>
            <div class="mt-2 flex flex-wrap gap-3 text-xs text-gray-600">
              <span
                v-for="part in BAR_PARTS"
                :key="part.key"
                class="inline-flex items-center gap-1"
                ><span
                  class="inline-block h-2 w-2 rounded-sm"
                  :class="part.color"
                ></span
                >{{ part.label }}</span
              >
            </div>

            <h4 class="mb-1 mt-4 text-sm font-semibold text-gray-900">
              Failures by reason
            </h4>
            <p
              v-if="!stats.failures_by_reason.length"
              class="m-0 text-sm text-gray-500"
              data-cy="webhook-failures-empty"
            >
              No failures in the last 7 days.
            </p>
            <ul v-else class="m-0 list-none space-y-2 p-0">
              <li
                v-for="reason in stats.failures_by_reason"
                :key="reason.category"
                class="rounded border border-gray-200 p-2 text-sm"
                :data-cy="`webhook-failure-reason:${reason.category}`"
              >
                <div class="flex justify-between gap-2">
                  <span class="font-medium text-gray-900">{{
                    reason.title
                  }}</span>
                  <span class="whitespace-nowrap text-gray-600">{{
                    reason.count
                  }}</span>
                </div>
                <p v-if="reason.hint" class="m-0 text-xs text-gray-600">
                  {{ reason.hint }}
                </p>
              </li>
            </ul>
          </template>
        </section>

        <section v-if="canWrite" data-cy="webhook-drawer-recover">
          <h3 class="mb-1 text-xs font-semibold uppercase text-gray-500">
            Recover failed deliveries
          </h3>
          <p class="m-0 mb-2 text-sm text-gray-600">
            Sends every failed delivery since a time you choose again, with its
            original webhook-id so your endpoint can skip any it already
            processed. Each gets a fresh set of up to 8 attempts. Deliveries are
            kept for 90 days.
          </p>
          <div class="flex flex-wrap items-center gap-2">
            <label for="webhook-recover-since" class="text-sm text-gray-700"
              >Since</label
            >
            <input
              id="webhook-recover-since"
              v-model="recoverSince"
              type="datetime-local"
              class="rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm"
              :min="recoverMin"
              :max="recoverMax"
              data-cy="webhook-recover-since"
            />
            <button
              type="button"
              :class="SECONDARY"
              :disabled="recovering || !sendable || !recoverSince"
              data-cy="webhook-recover-send"
              @click="recover"
            >
              {{ recovering ? "Queuing…" : "Recover" }}
            </button>
          </div>
          <p v-if="!sendable" class="m-0 mt-1 text-xs text-gray-500">
            {{ unsendableCopy }}
          </p>
          <p
            v-if="recoverResult"
            class="m-0 mt-2 text-sm text-gray-700"
            data-cy="webhook-recover-result"
          >
            {{ recoverResult }}
          </p>
        </section>
      </template>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import moment from "moment";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { formatDateTimeWithTimezone } from "@/lib/helpers";

import Alert from "@/components/Seranova/Alert/Alert.vue";
import Drawer from "@/components/Seranova/Drawer/Drawer.vue";
import Spinner from "@/components/Seranova/Spinner/Spinner.vue";
import type {
  EndpointStats,
  EndpointStatsDay,
  EndpointTransition,
  RecoverResult,
  WebhookEndpoint,
  WebhookEventType
} from "@/vuex/modules/integrationWebhooks";
import InlineConfirm from "./InlineConfirm.vue";
import VerificationSnippet from "./VerificationSnippet.vue";
import {
  apiError,
  disabledReasonCopy,
  endpointLabel,
  healthStyle,
  READ_ONLY_COPY,
  ROTATION_EXPLANATION,
  SIGNATURE_WARNING
} from "./webhooksHub";

const props = withDefaults(
  defineProps<{
    endpointId: string;
    eventTypes: WebhookEventType[];
    readOnly?: boolean;
  }>(),
  { readOnly: false }
);
const emit = defineEmits<{
  close: [];
  changed: [endpoint: WebhookEndpoint];
  archived: [id: string];
  edit: [endpoint: WebhookEndpoint];
}>();

const store = useStore();

const SECONDARY =
  "rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-60";
const FINGERPRINT_TITLE =
  "Tells apart endpoints on the same host. Lists never show the full URL.";

type Confirmable = EndpointTransition | "archive" | "rotate";

const CONFIRM_COPY: Record<
  Exclude<Confirmable, "rotate">,
  { label: string; message: string; done: string }
> = {
  pause: {
    label: "Pause endpoint",
    message:
      "Sera stops sending to this endpoint and cancels its queued deliveries. Events that happen while it's paused aren't sent later.",
    done: "Endpoint paused."
  },
  resume: {
    label: "Resume endpoint",
    message:
      "Sera sends this endpoint events from now on. Events from while it was paused aren't sent.",
    done: "Endpoint resumed."
  },
  enable: {
    label: "Enable endpoint",
    message:
      "Sera sends this endpoint events from now on and resets its failure count. To resend what failed, use Recover failed deliveries below.",
    done: "Endpoint enabled."
  },
  archive: {
    label: "Archive endpoint",
    message:
      "This endpoint stops receiving events and leaves the list. Its delivery history stays for 90 days. You can't undo this.",
    done: "Endpoint archived."
  }
};

const BAR_PARTS = [
  { key: "succeeded", label: "Succeeded", color: "bg-green-500" },
  { key: "failed", label: "Failed", color: "bg-red-500" },
  { key: "retrying", label: "Retrying", color: "bg-yellow-400" },
  { key: "pending", label: "Pending", color: "bg-blue-400" },
  { key: "cancelled", label: "Cancelled", color: "bg-gray-400" }
] as const;
type BarKey = (typeof BAR_PARTS)[number]["key"];

const endpoint = ref<WebhookEndpoint | null>(null);
const stats = ref<EndpointStats | null>(null);
const loading = ref(false);
const loadFailed = ref(false);
const statsFailed = ref(false);
const confirming = ref<Confirmable | null>(null);
const acting = ref(false);

// The revealed secret lives only in this component, never in the store, and
// is dropped when the drawer closes.
const secret = ref<string | null>(null);
const revealing = ref(false);
const secretCopied = ref(false);

const testEventType = ref("");
const testing = ref(false);

const recoverSince = ref("");
const recovering = ref(false);
const recoverResult = ref("");

const disabledCopy = computed(() => {
  const found = endpoint.value;
  if (!found) return "";
  const when = found.disabled_at
    ? ` on ${formatDateTimeWithTimezone(found.disabled_at)}`
    : "";
  return `${disabledReasonCopy(found.disabled_reason)}${when}. Nothing is sent until you enable it.`;
});

const failingCopy = computed(() => {
  const found = endpoint.value;
  if (!found) return "";
  const since = found.failing_since
    ? `, starting ${formatDateTimeWithTimezone(found.failing_since)}`
    : "";
  return `The last ${found.consecutive_failures} deliveries failed${since}. Sera sends one delivery at a time until one succeeds. After 5 days of failures the endpoint is disabled.`;
});

const isZapier = computed(() => endpoint.value?.kind === "zapier");
const canWrite = computed(() => !props.readOnly && !isZapier.value);

const sendable = computed(
  () =>
    endpoint.value?.status === "active" && !endpoint.value.paused_by_integration
);
const unsendableCopy = computed(() => {
  if (endpoint.value?.paused_by_integration) {
    return "Webhooks are turned off, so nothing can be sent.";
  }
  if (endpoint.value?.status === "paused") {
    return "Resume this endpoint to send to it.";
  }
  return "Enable this endpoint to send to it.";
});

// Subscribed types first, then the rest of the catalog: a test may use any.
const testEventTypes = computed(() => {
  const subscribed = endpoint.value?.event_types ?? [];
  return [...props.eventTypes].sort(
    (a, b) =>
      Number(!subscribed.includes(a.event_type)) -
      Number(!subscribed.includes(b.event_type))
  );
});

const recoverMax = computed(() => moment().format("YYYY-MM-DDTHH:mm"));
const recoverMin = computed(() =>
  moment().subtract(90, "days").format("YYYY-MM-DDTHH:mm")
);

const maxDayTotal = computed(() =>
  Math.max(1, ...(stats.value?.days ?? []).map(dayTotal))
);

const chartLabel = computed(() => {
  const days = stats.value?.days ?? [];
  const succeeded = days.reduce((sum, d) => sum + d.succeeded, 0);
  const failed = days.reduce((sum, d) => sum + d.failed, 0);
  return `Deliveries in the last 7 days: ${succeeded} succeeded, ${failed} failed`;
});

function dayTotal(day: EndpointStatsDay): number {
  return BAR_PARTS.reduce((sum, part) => sum + (day[part.key] ?? 0), 0);
}

function barHeight(day: EndpointStatsDay): number {
  const total = dayTotal(day);
  return total ? Math.max(4, (total / maxDayTotal.value) * 100) : 0;
}

function partShare(day: EndpointStatsDay, key: BarKey): number {
  const total = dayTotal(day);
  return total ? ((day[key] ?? 0) / total) * 100 : 0;
}

function shortDay(date: string): string {
  return moment(date, "YYYY-MM-DD").format("ddd");
}

function dayTitle(day: EndpointStatsDay): string {
  const parts = BAR_PARTS.filter((part) => day[part.key]).map(
    (part) => `${day[part.key]} ${part.label.toLowerCase()}`
  );
  return `${moment(day.date, "YYYY-MM-DD").format("MMM D")}: ${parts.join(", ") || "no deliveries"}`;
}

function defaultRecoverSince(found: WebhookEndpoint): string {
  const from =
    found.failing_since ?? found.disabled_at ?? found.last_failure_at ?? null;
  const since = from ? moment(from) : moment().subtract(1, "day");
  return since.format("YYYY-MM-DDTHH:mm");
}

function applyEndpoint(found: WebhookEndpoint) {
  // Transition and rotate responses carry no url; keep the one from show.
  endpoint.value = { ...found, url: found.url ?? endpoint.value?.url };
  if (
    !testEventType.value ||
    !props.eventTypes.some((t) => t.event_type === testEventType.value)
  ) {
    testEventType.value =
      found.event_types[0] ?? props.eventTypes[0]?.event_type ?? "";
  }
  if (!recoverSince.value) recoverSince.value = defaultRecoverSince(found);
}

async function loadStats() {
  statsFailed.value = false;
  try {
    stats.value = await store.dispatch(
      "fetchWebhookEndpointStats",
      props.endpointId
    );
  } catch {
    statsFailed.value = true;
  }
}

async function load() {
  loading.value = true;
  loadFailed.value = false;
  try {
    applyEndpoint(
      await store.dispatch("fetchWebhookEndpoint", props.endpointId)
    );
  } catch {
    loadFailed.value = true;
  } finally {
    loading.value = false;
  }
  if (!loadFailed.value) loadStats();
}

async function runConfirmed() {
  const action = confirming.value;
  if (!action || !endpoint.value) return;
  acting.value = true;
  try {
    if (action === "archive") {
      await store.dispatch("archiveWebhookEndpoint", endpoint.value.id);
      window.notify.success(CONFIRM_COPY.archive.done);
      emit("archived", endpoint.value.id);
      return;
    }
    if (action === "rotate") {
      applyEndpoint(
        await store.dispatch("rotateWebhookSecret", endpoint.value.id)
      );
      // The old value no longer tells the whole story; reveal the new one.
      secret.value = null;
      window.notify.success("Secret rotated. Reveal it to copy the new one.");
    } else {
      applyEndpoint(
        await store.dispatch("transitionWebhookEndpoint", {
          id: endpoint.value.id,
          transition: action
        })
      );
      window.notify.success(CONFIRM_COPY[action].done);
    }
    emit("changed", endpoint.value!);
    confirming.value = null;
  } catch (error) {
    window.notify.error(
      apiError(error, "That didn't work. Try again.").message
    );
  } finally {
    acting.value = false;
  }
}

async function reveal() {
  if (!endpoint.value) return;
  revealing.value = true;
  try {
    const revealed = await store.dispatch(
      "revealWebhookSecret",
      endpoint.value.id
    );
    secret.value = revealed.secret;
  } catch (error) {
    window.notify.error(
      apiError(error, "The secret couldn't be revealed. Try again.").message
    );
  } finally {
    revealing.value = false;
  }
}

async function copySecret() {
  if (!secret.value) return;
  try {
    await navigator.clipboard.writeText(secret.value);
    secretCopied.value = true;
    setTimeout(() => (secretCopied.value = false), 2000);
  } catch {
    window.notify.error(
      "Couldn't copy. Select the secret and copy it instead."
    );
  }
}

function hideSecret() {
  secret.value = null;
  secretCopied.value = false;
}

async function sendTest() {
  if (!endpoint.value || !testEventType.value) return;
  testing.value = true;
  try {
    await store.dispatch("sendWebhookTest", {
      id: endpoint.value.id,
      eventType: testEventType.value
    });
    window.notify.success("Test event queued. See it on the Deliveries tab.");
  } catch (error) {
    window.notify.error(
      apiError(error, "The test event couldn't be sent. Try again.").message
    );
  } finally {
    testing.value = false;
  }
}

async function recover() {
  if (!endpoint.value || !recoverSince.value) return;
  recovering.value = true;
  recoverResult.value = "";
  try {
    const result: RecoverResult = await store.dispatch(
      "recoverWebhookDeliveries",
      {
        id: endpoint.value.id,
        since: moment(recoverSince.value).toISOString()
      }
    );
    const queued = result.queued
      ? `${result.queued.toLocaleString("en-US")} failed ${result.queued === 1 ? "delivery" : "deliveries"} queued to send again.`
      : "No failed deliveries since then, so nothing was queued.";
    // Deliveries older than the retention are gone, so the API starts at
    // the oldest one it still has.
    recoverResult.value = result.clamped
      ? `Only the last 90 days were recovered, from ${formatDateTimeWithTimezone(result.since)}: older deliveries aren't kept. ${queued}`
      : queued;
  } catch (error) {
    window.notify.error(
      apiError(error, "Failed deliveries couldn't be recovered. Try again.")
        .message
    );
  } finally {
    recovering.value = false;
  }
}

onMounted(load);
onBeforeUnmount(() => {
  secret.value = null;
});
</script>
